import {
    avatarEventBus,
    LIP_SYNC_MESSAGE_START,
    LIP_SYNC_MESSAGE_STOP,
    LIP_SYNC_STREAMING_AUDIO_STOP,
} from '@/components/common/AIConversation/components/AvatarBus/constant';

export interface AudioRealtimeData {
    amplitude: number;
    frequency: number;
    rms: number;
    volume: number;
    mouthShape: number;
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    bufferLength: number;
    pcmData: Float32Array;
    timestamp: number;
}

/**
 * Calculate pitch using autocorrelation method
 * Algorithm:
 * 1. Choose window length M (e.g., 1024 samples)
 * 2. For each lag k from 1 → M/2:
 *    - correlation[k] = Σ(x[n] * x[n + k]) for n = 0 → M−k−1
 * 3. Find k_best where correlation[k] is maximum
 * 4. If correlation[k_best] > threshold → f0 = Fs / k_best, else f0 = 0
 * @param samples - Audio samples (Float32Array)
 * @param sampleRate - Sample rate in Hz (Fs)
 * @returns Pitch in Hz (f0, 0 if not detected/unvoiced)
 */
function calculatePitch(samples: Float32Array, sampleRate: number): number {
    if (samples.length < 2) return 0;

    // Choose window length M (use available samples, max 1024)
    const M = Math.min(samples.length, 1024);
    const maxLag = Math.floor(M / 2);

    let maxCorrelation = 0;
    let kBest = 0;

    // For each lag k from 1 → M/2
    for (let k = 1; k <= maxLag; k++) {
        let sum = 0;

        // For n = 0 → M−k−1
        for (let n = 0; n < M - k; n++) {
            sum += samples[n] * samples[n + k];
        }

        // Store correlation[k] = sum
        if (sum > maxCorrelation) {
            maxCorrelation = sum;
            kBest = k;
        }
    }

    // Threshold for voiced/unvoiced detection (normalize by window size)
    const threshold = 0.1 * M;

    // If correlation[k_best] is above threshold → voiced
    if (maxCorrelation > threshold && kBest > 0) {
        // f0 = Fs / k_best
        return sampleRate / kBest;
    }

    // Below threshold → unvoiced/no pitch
    return 0;
}

/**
 * Calculate spectral centroid (brightness of sound)
 * Algorithm:
 * Total magnitude: den = Σ |X[k]|
 * Weighted sum: num = Σ (k * |X[k]|)
 * Bin frequency resolution: Δf = Fs / (2 * K) (K = number of bins)
 * Centroid: centroid = (num / den) * Δf
 * @param frequencyData - Frequency domain data |X[k]| (Uint8Array)
 * @param sampleRate - Sample rate in Hz (Fs)
 * @returns Spectral centroid in Hz
 */
function calculateSpectralCentroid(frequencyData: Uint8Array, sampleRate: number): number {
    const K = frequencyData.length;
    const nyquist = sampleRate / 2;
    const deltaF = nyquist / K; // Bin frequency resolution: Δf = Fs / (2 * K)

    // Total magnitude: den = Σ |X[k]|
    let den = 0;
    // Weighted sum: num = Σ (k * |X[k]|)
    let num = 0;

    for (let k = 0; k < K; k++) {
        const magnitude = frequencyData[k]; // |X[k]|
        den += magnitude;
        num += k * magnitude;
    }

    // Centroid: centroid = (num / den) * Δf
    if (den > 0) {
        return (num / den) * deltaF;
    }

    return 0;
}

/**
 * Calculate spectral rolloff (85% energy)
 * Algorithm:
 * Compute total energy: E_total = Σ |X[k]|
 * Threshold energy: E_thresh = 0.85 * E_total
 * Accumulate energy from k = 0 upward:
 * Find smallest k_roll where: Σ(|X[i]| from i=0..k_roll) >= E_thresh
 * Rolloff frequency: rolloff = k_roll * Δf
 * @param frequencyData - Frequency domain data |X[k]| (Uint8Array)
 * @param sampleRate - Sample rate in Hz (Fs)
 * @returns Spectral rolloff in Hz
 */
function calculateSpectralRolloff(frequencyData: Uint8Array, sampleRate: number): number {
    const K = frequencyData.length;
    const nyquist = sampleRate / 2;
    const deltaF = nyquist / K; // Bin frequency resolution: Δf = Fs / (2 * K)

    // Compute total energy: E_total = Σ |X[k]|
    let E_total = 0;
    for (let k = 0; k < K; k++) {
        E_total += frequencyData[k];
    }

    if (E_total === 0) return 0;

    // Threshold energy: E_thresh = 0.85 * E_total
    const E_thresh = 0.85 * E_total;

    // Accumulate energy from k = 0 upward
    let cumulativeEnergy = 0;
    for (let k_roll = 0; k_roll < K; k_roll++) {
        cumulativeEnergy += frequencyData[k_roll];
        // Find smallest k_roll where cumulative energy >= E_thresh
        if (cumulativeEnergy >= E_thresh) {
            // Rolloff frequency: rolloff = k_roll * Δf
            return k_roll * deltaF;
        }
    }

    // If threshold not reached, return nyquist
    return nyquist;
}

/**
 * Calculate smoothed amplitude envelope using low-pass filter
 * Algorithm: E = α * rms + (1 − α) * E_prev
 * This reduces rapid fluctuations to produce a stable mouth-open value
 * @param rms - Current RMS value
 * @param previousEnvelope - Previous envelope value (E_prev)
 * @param smoothingFactor - Smoothing factor α (0.1-0.3, default 0.2)
 * @returns Smoothed amplitude envelope value (0-1)
 */
function calculateAmplitudeEnvelope(rms: number, previousEnvelope: number = 0, smoothingFactor: number = 0.2): number {
    // E = α * rms + (1 − α) * E_prev
    const envelope = smoothingFactor * rms + (1 - smoothingFactor) * previousEnvelope;
    return Math.min(envelope, 1);
}

/**
 * Calculate mouth shape value based on multiple audio parameters
 * Uses Volume (RMS), Amplitude Envelope, Pitch, and Spectral Features
 * Maps to phoneme-specific mouth shapes:
 * 0.0-0.2: open_B_M_P (closed)
 * 0.2-0.4: open_EE (slight)
 * 0.4-0.6: open_A_E_I (medium)
 * 0.6-0.8: open_O (wide)
 * 0.8-1.0: open_L (very wide)
 * @param rms - Root mean square energy (0-1)
 * @param amplitudeEnvelope - Amplitude envelope (0-1)
 * @param pitch - Pitch in Hz (0 if not detected)
 * @param spectralCentroid - Spectral centroid in Hz
 * @param spectralRolloff - Spectral rolloff in Hz
 * @param avgFrequency - Average frequency (0-255) for fallback
 * @returns Mouth shape value (0-1)
 */
function calculateMouthShape(
    rms: number,
    amplitudeEnvelope: number,
    pitch: number,
    spectralCentroid: number,
    spectralRolloff: number,
    avgFrequency: number = 0,
): number {
    // 1. Volume (RMS Energy) - Primary indicator of mouth opening
    // Higher RMS = more mouth opening
    const normalizedRms = Math.min(rms * 10, 1); // Scale RMS to 0-1 range
    const volumeFactor = normalizedRms * 0.4; // 40% weight

    // 2. Amplitude Envelope - Smoothed amplitude tracking
    // Higher envelope = more mouth opening
    const envelopeFactor = amplitudeEnvelope * 0.3; // 30% weight

    // 3. Pitch-based mouth shape
    // Higher pitch (vowels like A, E, I) = more open
    // Lower pitch (consonants like B, M, P) = less open
    let pitchFactor = 0;
    if (pitch > 0) {
        // Normalize pitch: typical speech range is 80-400Hz
        // Map to 0-1 range: 80Hz = 0, 400Hz = 1
        const normalizedPitch = Math.max(0, Math.min(1, (pitch - 80) / 320));
        pitchFactor = normalizedPitch * 0.15; // 15% weight
    } else {
        // Fallback to average frequency if pitch not detected
        const normalizedFreq = Math.min(avgFrequency / 255, 1);
        pitchFactor = normalizedFreq * 0.1; // 10% weight (reduced since less accurate)
    }

    // 4. Spectral Centroid (brightness) - Higher = more open mouth
    // Typical range: 500-3000Hz for speech
    let spectralFactor = 0;
    if (spectralCentroid > 0) {
        const normalizedCentroid = Math.max(0, Math.min(1, (spectralCentroid - 500) / 2500));
        spectralFactor = normalizedCentroid * 0.1; // 10% weight
    }

    // 5. Spectral Rolloff - Indicates frequency distribution
    // Higher rolloff = more high-frequency content = more open
    let rolloffFactor = 0;
    if (spectralRolloff > 0) {
        // Typical range: 1000-4000Hz for speech
        const normalizedRolloff = Math.max(0, Math.min(1, (spectralRolloff - 1000) / 3000));
        rolloffFactor = normalizedRolloff * 0.05; // 5% weight
    }

    // Combine all factors
    const combinedShape = volumeFactor + envelopeFactor + pitchFactor + spectralFactor + rolloffFactor;

    // Apply smoothing and ensure valid range
    return Math.max(0, Math.min(1, combinedShape));
}

/**
 * PCM Audio Player for streaming audio playback
 * Handles Int16Array PCM data with configurable sample rate
 * Supports URL, Blob, Base64 audio sources
 */
export class Player {
    private audioContext: AudioContext | null = null;
    private sampleRate: number = 24000;
    private scriptProcessor: ScriptProcessorNode | null = null;
    private audioBuffer: Float32Array = new Float32Array(0);
    private isAudioPlaying: boolean = false;
    private volumeThreshold: number = 0.001; // Threshold for detecting silence
    private stopTimeout: NodeJS.Timeout | null = null; // Timeout for delayed stop
    private stopDelay: number = 1000; // 1 seconds delay before stopping
    private analyzerNode: AnalyserNode | null = null;
    private currentAudioElement: HTMLAudioElement | null = null;
    private sourceNode: MediaElementAudioSourceNode | null = null;
    private previousEnvelope: number = 0; // Previous envelope value for smoothing (E_prev)

    // Realtime data for lip sync
    public realtimeData: AudioRealtimeData = {
        amplitude: 0,
        frequency: 0,
        rms: 0,
        volume: 0,
        mouthShape: 0,
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        bufferLength: 0,
        pcmData: new Float32Array(0),
        timestamp: Date.now(),
    };

    /**
     * Initialize the player with a specific sample rate
     * @param sampleRate - Audio sample rate (default: 24000)
     */
    init(sampleRate: number = 24000): void {
        this.sampleRate = sampleRate;

        try {
            // Create AudioContext with the specified sample rate
            this.audioContext = new AudioContext({ sampleRate: this.sampleRate });

            // Create analyzer node for realtime data
            this.analyzerNode = this.audioContext.createAnalyser();
            this.analyzerNode.fftSize = 2048;
            this.analyzerNode.smoothingTimeConstant = 0.8;

            // Create a script processor node for streaming audio
            // Note: ScriptProcessorNode is deprecated but still widely supported
            // For better performance, consider using AudioWorklet in the future
            const bufferSize = 4096;
            this.scriptProcessor = this.audioContext.createScriptProcessor(bufferSize, 0, 1);
            this.scriptProcessor.onaudioprocess = (e) => {
                const output = e.outputBuffer.getChannelData(0);
                const outputLength = output.length;

                if (this.audioBuffer.length >= outputLength) {
                    // Copy data from buffer to output
                    for (let i = 0; i < outputLength; i++) {
                        output[i] = this.audioBuffer[i];
                    }
                    // Remove processed data from buffer
                    this.audioBuffer = this.audioBuffer.slice(outputLength);
                } else if (this.audioBuffer.length > 0) {
                    // Partial buffer available
                    for (let i = 0; i < this.audioBuffer.length; i++) {
                        output[i] = this.audioBuffer[i];
                    }
                    // Fill remaining with zeros
                    for (let i = this.audioBuffer.length; i < outputLength; i++) {
                        output[i] = 0;
                    }
                    this.audioBuffer = new Float32Array(0);
                } else {
                    // No data available, output silence
                    for (let i = 0; i < outputLength; i++) {
                        output[i] = 0;
                    }
                }

                // 1. Calculate RMS Energy
                // Algorithm: Initialize sum = 0, For each sample n: sum += x[n]^2, RMS = sqrt(sum / N)
                let sum = 0;
                for (let n = 0; n < outputLength; n++) {
                    sum += output[n] * output[n];
                }
                const rms = Math.sqrt(sum / outputLength);

                // Calculate frequency data
                const dataArray = new Uint8Array(this.analyzerNode?.frequencyBinCount || 0);
                this.analyzerNode?.getByteFrequencyData(dataArray);
                const avgFrequency = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

                // Calculate volume (normalized RMS, 0-1 range)
                const volume = Math.min(rms * 2, 1); // Amplify and clamp to 0-1

                // 2. Calculate Amplitude Envelope (Smoothed RMS)
                // Algorithm: E = α * rms + (1 − α) * E_prev
                const amplitudeEnvelope = calculateAmplitudeEnvelope(rms, this.previousEnvelope, 0.2);
                this.previousEnvelope = amplitudeEnvelope; // Update previous envelope for next iteration

                // 3. Calculate Pitch using autocorrelation
                const pitch = calculatePitch(output, this.sampleRate);

                // 4. Calculate Spectral Centroid (Brightness)
                const spectralCentroid = calculateSpectralCentroid(dataArray, this.sampleRate);

                // 5. Calculate Spectral Rolloff (85% energy)
                const spectralRolloff = calculateSpectralRolloff(dataArray, this.sampleRate);

                // Calculate mouth shape using Volume (RMS), Amplitude Envelope, Pitch, and Spectral Features
                const mouthShape =
                    rms <= this.volumeThreshold
                        ? 0
                        : calculateMouthShape(
                            rms,
                            amplitudeEnvelope,
                            pitch,
                            spectralCentroid,
                            spectralRolloff,
                            avgFrequency,
                        );

                // Update realtime data
                this.realtimeData = {
                    amplitude: rms > this.volumeThreshold ? rms : 0,
                    frequency: avgFrequency,
                    rms,
                    volume,
                    mouthShape,
                    isPlaying: this.isAudioPlaying,
                    currentTime: this.audioContext?.currentTime || 0,
                    duration: 0,
                    bufferLength: this.audioBuffer.length,
                    pcmData: output.slice(0),
                    timestamp: Date.now(),
                };

                // Detect when audio volume is 0 or below threshold
                if (rms <= this.volumeThreshold) {
                    if (this.isAudioPlaying && !this.stopTimeout) {
                        // Volume dropped to 0, start delayed stop timer
                        console.log('PCM Player: Volume dropped, starting 2s stop delay...');
                        this.stopTimeout = setTimeout(() => {
                            if (this.isAudioPlaying) {
                                this.setAudioPlaying(false);
                                console.log('PCM Player: Audio stopped after delay');
                            }
                            this.stopTimeout = null;
                        }, this.stopDelay);
                    }
                } else {
                    // Audio is playing - clear any pending stop timeout
                    if (this.stopTimeout) {
                        console.log('PCM Player: Volume resumed, cancelling stop delay');
                        clearTimeout(this.stopTimeout);
                        this.stopTimeout = null;
                    }
                    this.setAudioPlaying(true);
                }
            };

            // Connect to analyzer and destination
            this.scriptProcessor.connect(this.analyzerNode);
            this.analyzerNode.connect(this.audioContext.destination);

            console.log(`PCM Player initialized with sample rate: ${sampleRate}`);
        } catch (error) {
            console.error('Error initializing PCM Player:', error);
        }
    }

    /**
     * Play PCM audio data
     * @param pcmData - Int16Array containing PCM audio samples
     */
    play(pcmData: Int16Array): void {
        if (!this.audioContext || !this.scriptProcessor) {
            console.warn('Player not initialized. Call init() first.');
            return;
        }

        // Resume audio context if suspended (required for autoplay policy)
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume().catch((error) => {
                console.error('Error resuming audio context:', error);
            });
        }

        // Convert Int16Array to Float32Array (normalize to -1.0 to 1.0 range)
        const floatData = new Float32Array(pcmData.length);
        for (let i = 0; i < pcmData.length; i++) {
            // Int16 range is -32768 to 32767, normalize to -1.0 to 1.0
            floatData[i] = pcmData[i] / 32768.0;
        }

        // Append to buffer (race conditions are minimal and acceptable for streaming audio)
        const newBuffer = new Float32Array(this.audioBuffer.length + floatData.length);
        newBuffer.set(this.audioBuffer);
        newBuffer.set(floatData, this.audioBuffer.length);
        this.audioBuffer = newBuffer;
    }

    /**
     * Play audio from URL
     * @param url - Audio URL
     */
    async playFromUrl(url: string): Promise<void> {
        if (!this.audioContext) {
            console.warn('Player not initialized. Call init() first.');
            return;
        }

        try {
            // Stop any existing audio element
            this.stopAudioElement();

            // Create audio element
            this.currentAudioElement = new Audio(url);
            this.currentAudioElement.crossOrigin = 'anonymous';

            // Create source node
            this.sourceNode = this.audioContext.createMediaElementSource(this.currentAudioElement);

            // Connect to analyzer and destination
            this.sourceNode.connect(this.analyzerNode!);

            // Setup event listeners for realtime data updates
            this.currentAudioElement.addEventListener('play', () => {
                this.setAudioPlaying(true);
            });

            this.currentAudioElement.addEventListener('pause', () => {
                this.setAudioPlaying(false);
            });

            this.currentAudioElement.addEventListener('ended', () => {
                this.setAudioPlaying(false);
            });

            this.currentAudioElement.addEventListener('timeupdate', () => {
                if (this.currentAudioElement) {
                    this.realtimeData.currentTime = this.currentAudioElement.currentTime;
                    this.realtimeData.duration = this.currentAudioElement.duration;
                }
            });

            // Start playback
            await this.currentAudioElement.play();

            // Start realtime data updates
            this.startRealtimeDataUpdates();

            console.log('Playing audio from URL:', url);
        } catch (error) {
            console.error('Error playing audio from URL:', error);
        }
    }

    /**
     * Play audio from Blob
     * @param blob - Audio Blob
     */
    async playFromBlob(blob: Blob): Promise<void> {
        const url = URL.createObjectURL(blob);
        await this.playFromUrl(url);
    }

    /**
     * Play audio from Base64
     * @param base64 - Base64 encoded audio string
     * @param mimeType - MIME type of the audio (e.g., 'audio/mp3', 'audio/wav')
     */
    async playFromBase64(base64: string, mimeType: string = 'audio/mp3'): Promise<void> {
        try {
            // Convert base64 to blob
            const byteCharacters = atob(base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });

            await this.playFromBlob(blob);
        } catch (error) {
            console.error('Error playing audio from Base64:', error);
        }
    }

    /**
     * Start realtime data updates for audio element playback
     */
    private startRealtimeDataUpdates(): void {
        if (!this.analyzerNode || !this.currentAudioElement) return;

        const updateData = () => {
            if (!this.analyzerNode || !this.isAudioPlaying) return;

            const dataArray = new Uint8Array(this.analyzerNode.frequencyBinCount);
            const waveformArray = new Uint8Array(this.analyzerNode.fftSize);

            this.analyzerNode.getByteFrequencyData(dataArray);
            this.analyzerNode.getByteTimeDomainData(waveformArray);

            // Calculate average frequency
            const avgFrequency = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

            // 1. Calculate RMS Energy
            // Algorithm: Initialize sum = 0, For each sample n: sum += x[n]^2, RMS = sqrt(sum / N)
            let sum = 0;
            const normalizedWaveform = new Float32Array(waveformArray.length);
            for (let n = 0; n < waveformArray.length; n++) {
                const normalized = (waveformArray[n] - 128) / 128;
                normalizedWaveform[n] = normalized;
                sum += normalized * normalized;
            }
            const rms = Math.sqrt(sum / waveformArray.length);

            // Calculate volume (normalized RMS, 0-1 range)
            const volume = Math.min(rms * 2, 1); // Amplify and clamp to 0-1

            // 2. Calculate Amplitude Envelope (Smoothed RMS)
            // Algorithm: E = α * rms + (1 − α) * E_prev
            const amplitudeEnvelope = calculateAmplitudeEnvelope(rms, this.previousEnvelope, 0.2);
            this.previousEnvelope = amplitudeEnvelope; // Update previous envelope for next iteration

            // 3. Calculate Pitch using autocorrelation
            const pitch = calculatePitch(normalizedWaveform, this.audioContext?.sampleRate || 24000);

            // 4. Calculate Spectral Centroid (Brightness)
            const spectralCentroid = calculateSpectralCentroid(dataArray, this.audioContext?.sampleRate || 24000);

            // 5. Calculate Spectral Rolloff (85% energy)
            const spectralRolloff = calculateSpectralRolloff(dataArray, this.audioContext?.sampleRate || 24000);

            // Calculate mouth shape using Volume (RMS), Amplitude Envelope, Pitch, and Spectral Features
            const mouthShape = calculateMouthShape(
                rms,
                amplitudeEnvelope,
                pitch,
                spectralCentroid,
                spectralRolloff,
                avgFrequency,
            );

            // Convert waveform to Float32Array for pcmData
            const pcmData = new Float32Array(waveformArray.length);
            for (let i = 0; i < waveformArray.length; i++) {
                pcmData[i] = (waveformArray[i] - 128) / 128;
            }

            // Update realtime data
            this.realtimeData = {
                amplitude: rms > this.volumeThreshold ? rms : 0,
                frequency: avgFrequency,
                rms,
                volume,
                mouthShape,
                isPlaying: this.isAudioPlaying,
                currentTime: this.currentAudioElement?.currentTime || 0,
                duration: this.currentAudioElement?.duration || 0,
                bufferLength: 0,
                pcmData,
                timestamp: Date.now(),
            };

            if (this.isAudioPlaying) {
                requestAnimationFrame(updateData);
            }
        };

        updateData();
    }

    /**
     * Stop audio element playback
     */
    private stopAudioElement(): void {
        if (this.currentAudioElement) {
            this.currentAudioElement.pause();
            this.currentAudioElement.src = '';
            this.currentAudioElement = null;
        }
        if (this.sourceNode) {
            this.sourceNode.disconnect();
            this.sourceNode = null;
        }
    }

    /**
     * Stop playback and clear buffer
     */
    stop(): void {
        // Clear any pending stop timeout
        if (this.stopTimeout) {
            clearTimeout(this.stopTimeout);
            this.stopTimeout = null;
        }

        // Stop audio element if playing
        this.stopAudioElement();

        this.audioBuffer = new Float32Array(0);
        this.setAudioPlaying(false);
        this.previousEnvelope = 0; // Reset envelope smoothing

        if (this.scriptProcessor) {
            this.scriptProcessor.disconnect();
            this.scriptProcessor = null;
        }
        if (this.analyzerNode) {
            this.analyzerNode.disconnect();
            this.analyzerNode = null;
        }
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close();
            this.audioContext = null;
        }

    }

    /**
     * Pause current playback (for audio element)
     */
    pause(): void {
        if (this.currentAudioElement) {
            this.currentAudioElement.pause();
            this.setAudioPlaying(false);
        }
    }

    /**
     * Resume playback (for audio element)
     */
    async resume(): Promise<void> {
        if (this.currentAudioElement && this.currentAudioElement.paused) {
            try {
                await this.currentAudioElement.play();
                this.setAudioPlaying(true);
                this.startRealtimeDataUpdates();
            } catch (error) {
                console.error('Error resuming audio:', error);
            }
        }
    }

    /**
     * Get current realtime audio data
     */
    getRealtimeData(): AudioRealtimeData {
        return { ...this.realtimeData };
    }

    /**
     * Check if audio is currently playing
     */
    isPlaying(): boolean {
        return this.isAudioPlaying;
    }

    /**
     * Check if player is initialized
     */
    isInitialized(): boolean {
        return this.audioContext !== null && (this.scriptProcessor !== null || this.analyzerNode !== null);
    }

    setAudioPlaying(isPlaying: boolean): void {
        if (!this.isAudioPlaying && isPlaying) {
            avatarEventBus.emit(LIP_SYNC_MESSAGE_START);
        } else if (this.isAudioPlaying && !isPlaying) {
            avatarEventBus.emit(LIP_SYNC_MESSAGE_STOP);
            avatarEventBus.emit(LIP_SYNC_STREAMING_AUDIO_STOP, {
                timestamp: Date.now(),
            });
        }
        this.realtimeData.isPlaying = isPlaying;
        this.isAudioPlaying = isPlaying;
    }
}
